import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Settings, Shield, Truck, Leaf, Wrench, Gauge, Ban, Cpu, Flame, Wind } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const ServicesPage = () => {
  useCanonical('https://ecuflashservice.com/services');
  
  const mainServices = [
    {
      icon: Settings,
      name: 'DPF Delete',
      slug: 'dpf-off',
      price: '$248',
      description: 'Remove Diesel Particulate Filter restrictions from your ECU. Eliminate regeneration cycles, reduce exhaust back pressure, and restore full engine performance.',
      benefits: ['No more regeneration', 'Improved fuel economy', 'Extended engine life', 'No DPF replacement costs'],
      color: '#00F0FF'
    },
    {
      icon: Leaf,
      name: 'EGR Delete',
      slug: 'egr-off',
      price: '$50',
      description: 'Disable Exhaust Gas Recirculation system. Prevent carbon buildup in intake manifold, improve throttle response, and maintain cleaner engine internals.',
      benefits: ['Cleaner intake system', 'Lower intake temps', 'Better throttle response', 'Reduced maintenance'],
      color: '#22C55E'
    },
    {
      icon: Truck,
      name: 'AdBlue/SCR Delete',
      slug: 'adblue-off',
      price: '$698',
      description: 'Remove AdBlue/DEF system requirements. Eliminate fluid costs, freezing issues, and inducement (speed limiting) problems.',
      benefits: ['No AdBlue costs', 'No speed limits', 'No start prevention', 'Works in all climates'],
      color: '#A855F7'
    },
    {
      icon: Ban,
      name: 'DTC Removal',
      slug: 'dtc-removal',
      price: 'From $10',
      description: 'Permanently remove diagnostic trouble codes from your ECU. Eliminate dashboard warning lights caused by modifications or faulty sensors.',
      benefits: ['No warning lights', 'No limp mode', 'Clean diagnostics', '30-day free support'],
      color: '#EF4444'
    }
  ];

  const additionalServices = [
    { icon: Gauge, name: 'Speed Limiter Removal', price: '$30', description: 'Remove factory speed limiters for unrestricted top speed' },
    { icon: Flame, name: 'Lambda/O2 Delete', price: '$50', description: 'Disable oxygen sensor monitoring for modified exhaust systems' },
    { icon: Shield, name: 'Catalyst Delete', price: '$50', description: 'Remove catalytic converter monitoring from ECU' },
    { icon: Wind, name: 'Flaps/Swirl Delete', price: '$40', description: 'Disable intake manifold flap control and monitoring' },
    { icon: Cpu, name: 'Immo Off', price: '$70', description: 'Disable immobilizer for ECU swaps or lost keys' },
    { icon: Wrench, name: 'Hot Start Fix', price: '$40', description: 'Resolve hot start issues common in some diesel engines' },
  ];

  const performanceTuning = [
    { name: 'Stage 1 Tune', price: '$248', power: '+20-30%', description: 'Safe power increase with stock hardware' },
    { name: 'Stage 2 Tune', price: '$248', power: '+30-40%', description: 'Maximum power with upgraded intake/exhaust' },
    { name: 'Custom Tune', price: 'Quote', power: 'Custom', description: 'Tailored calibration for your specific setup' },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ECU Tuning Services | DPF EGR AdBlue Delete | Stage 1 & 2 Remap</title>
        <meta name="description" content="Professional ECU tuning services. DPF delete, EGR removal, AdBlue off, DTC removal, Stage 1 & 2 performance tuning. BMW, Mercedes, VW, Audi, Ford. Fast 24h turnaround." />
        <meta name="keywords" content="ECU tuning services, DPF delete service, EGR delete service, AdBlue delete, Stage 1 tune, Stage 2 tune, chip tuning, engine remap" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/how-it-works" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">How It Works</Link>
              <Link to="/pricing" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">Pricing</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <Wrench className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Professional Tuning</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">ECU Tuning</span>{' '}
            <span className="text-[#00F0FF]">Services</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto">
            Professional ECU remapping & delete services. DPF, EGR, AdBlue removal. Stage 1 & 2 performance tuning. All brands supported.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-12">Core Services</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {mainServices.map((service, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#00F0FF]/30 transition-all group">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center border border-white/10 group-hover:border-[#00F0FF]/30 transition" style={{ backgroundColor: `${service.color}20` }}>
                    <service.icon className="w-7 h-7" style={{ color: service.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white">{service.name}</h3>
                      <span className="font-heading text-xl font-bold text-[#00F0FF]">{service.price}</span>
                    </div>
                    <p className="text-[#888] text-sm mt-2">{service.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {service.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#666]">
                      <ChevronRight className="w-4 h-4 text-[#00F0FF]" />
                      {benefit}
                    </div>
                  ))}
                </div>
                <Link 
                  to={`/services/${service.slug}`}
                  className="inline-flex items-center gap-2 text-[#00F0FF] hover:text-white font-semibold text-sm transition"
                >
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-12">Additional Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {additionalServices.map((service, idx) => (
              <div key={idx} className="bg-[#1A1A1A] border border-white/5 p-5 hover:border-[#00F0FF]/30 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <service.icon className="w-5 h-5 text-[#00F0FF]" />
                  <h3 className="font-semibold text-white">{service.name}</h3>
                </div>
                <p className="text-[#666] text-sm mb-3">{service.description}</p>
                <div className="font-heading text-lg font-bold text-[#00F0FF]">{service.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Tuning */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-4">Performance Tuning</h2>
          <p className="text-[#888] text-center mb-12">Unlock your engine's full potential with our performance calibrations</p>
          <div className="grid md:grid-cols-3 gap-6">
            {performanceTuning.map((tune, idx) => (
              <div key={idx} className="relative overflow-hidden bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#00F0FF]/30 transition-all">
                <div className="absolute top-0 right-0 bg-[#00F0FF] text-black text-xs font-bold px-3 py-1">
                  {tune.power}
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-2">{tune.name}</h3>
                <p className="text-[#666] text-sm mb-4">{tune.description}</p>
                <div className="font-heading text-2xl font-bold text-[#00F0FF]">{tune.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Vehicles */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-12">Supported Vehicles</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {[
              'Volkswagen', 'Audi', 'BMW', 'Mercedes', 'Ford', 'Toyota',
              'Mazda', 'Hyundai', 'Kia', 'Nissan', 'Peugeot', 'Renault',
              'Volvo', 'Jaguar', 'Land Rover', 'Jeep', 'Fiat', 'All Brands'
            ].map((brand, idx) => (
              <div key={idx} className="bg-[#1A1A1A] border border-white/5 p-3 text-center text-[#888] text-sm font-medium hover:border-[#00F0FF]/30 hover:text-white transition">
                {brand}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready to Tune Your Vehicle?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file and select your services. 20-60 minute turnaround.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/" 
              className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
            >
              <span>Get Started →</span>
            </Link>
            <Link 
              to="/pricing" 
              className="bg-transparent border border-white/20 text-white font-bold uppercase tracking-wider px-8 py-4 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-all"
            >
              View Full Price List
            </Link>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default ServicesPage;
